import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Plus, ChevronLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import { DraftComposer, KIND_LABEL } from '@/components/drafts/DraftComposer';
import { NewDraftComposer } from '@/components/drafts/NewDraftComposer';
import type { DraftDto } from '@cc/types';

export function DraftsPage() {
  const [tab, setTab] = useState<'pending' | 'terminal'>('pending');
  const [params, setParams] = useSearchParams();
  const urlId = params.get('id');
  const { data, isLoading } = useQuery({
    queryKey: qk.drafts(tab),
    queryFn: () => api.listDrafts(tab),
  });
  const [selectedId, setSelectedId] = useState<string | null>(urlId);
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    if (urlId) {
      setSelectedId(urlId);
      setTab('pending');
    }
  }, [urlId]);

  useEffect(() => {
    if (!data?.drafts.length) {
      if (!composing) setSelectedId(null);
      return;
    }
    if (!selectedId || !data.drafts.find((d) => d.id === selectedId)) {
      setSelectedId(data.drafts[0]!.id);
    }
  }, [data, selectedId, composing]);

  function selectDraft(id: string) {
    setSelectedId(id);
    setComposing(false);
    if (params.get('id') && params.get('id') !== id) {
      params.delete('id');
      setParams(params, { replace: true });
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Drafts</h1>
          <p className="text-sm text-muted-foreground">
            Reply, reply-all, forward, and compose new mail. Edits sync to Gmail Drafts; send
            goes through the connected account.
          </p>
        </div>
        <Button onClick={() => setComposing(true)} size="sm">
          <Plus className="h-4 w-4" /> New draft
        </Button>
      </div>
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'pending' | 'terminal')}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="terminal">Sent / discarded</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          {isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : data?.drafts.length === 0 && !composing ? (
            <EmptyState tab={tab} onCompose={() => setComposing(true)} />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.04 } } }}
                className={cn(
                  'space-y-1.5 md:col-span-2',
                  (selectedId || composing) ? 'hidden md:block' : 'block',
                )}
              >
                {data?.drafts.map((d) => (
                  <DraftRow
                    key={d.id}
                    draft={d}
                    selected={selectedId === d.id && !composing}
                    onSelect={() => selectDraft(d.id)}
                  />
                ))}
              </motion.div>
              <div
                className={cn(
                  'md:col-span-3',
                  (selectedId || composing) ? 'block' : 'hidden md:block',
                )}
              >
                <div className="mb-2 md:hidden">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedId(null);
                      setComposing(false);
                    }}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" /> Back to list
                  </Button>
                </div>
                {composing ? (
                  <NewDraftComposer
                    onCancel={() => setComposing(false)}
                    onCreated={(id) => {
                      setComposing(false);
                      setSelectedId(id);
                    }}
                  />
                ) : selectedId ? (
                  <DraftComposer id={selectedId} />
                ) : null}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DraftRow({
  draft,
  selected,
  onSelect,
}: {
  draft: DraftDto;
  selected: boolean;
  onSelect: () => void;
}) {
  const headline = draft.message?.subject || draft.subject || '(no subject)';
  const sender =
    draft.message?.fromName ||
    draft.message?.fromEmail ||
    (draft.toEmails[0] ?? 'New draft');
  const statusVariant =
    draft.status === 'SUGGESTED'
      ? 'warning'
      : draft.status === 'EDITED' || draft.status === 'APPROVED'
        ? 'success'
        : draft.status === 'SENT'
          ? 'muted'
          : 'danger';
  return (
    <motion.button
      type="button"
      onClick={onSelect}
      variants={{
        hidden: { opacity: 0, y: 4 },
        visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 140, damping: 16 } },
      }}
      className={cn(
        'w-full rounded-md border bg-card p-3 text-left transition-colors',
        selected ? 'border-foreground/40' : 'border-border hover:border-foreground/20',
      )}
    >
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-[10px]">
          {KIND_LABEL[draft.kind]}
        </Badge>
        <p className="truncate text-sm font-medium">{sender}</p>
        <Badge variant={statusVariant}>{draft.status.toLowerCase()}</Badge>
      </div>
      <p className="mt-0.5 truncate text-sm">{headline}</p>
      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
        updated {relativeTime(draft.updatedAt)}
      </p>
    </motion.button>
  );
}

function EmptyState({
  tab,
  onCompose,
}: {
  tab: 'pending' | 'terminal';
  onCompose: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
          <Mail className="h-5 w-5" />
        </div>
        <p className="text-sm">No {tab === 'pending' ? 'pending' : 'sent or discarded'} drafts.</p>
        {tab === 'pending' && (
          <>
            <p className="max-w-md text-xs text-muted-foreground">
              Drafts are auto-generated for IMPORTANT_REPLY messages. You can also start one from
              the inbox (Reply / Reply all / Forward) or write a fresh one below.
            </p>
            <Button size="sm" onClick={onCompose}>
              <Plus className="h-4 w-4" /> New draft
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

